import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
} from 'recharts';
import { CHART_NEUTRALS, CHART_RAMP } from '../design/tokens';
import { rechartsGrid, rechartsTooltip, rechartsXAxis, rechartsYAxis } from '../design/chartDefaults';

const VisionStrategyChart = ({ vision, cashflow }) => {
  if (!vision || !vision.target_date) return null;

  const targetDate = new Date(vision.target_date);
  const currentDate = new Date();
  const daysRemaining = Math.max(1, Math.ceil((targetDate - currentDate) / (1000 * 60 * 60 * 24)));
  const monthsRemaining = daysRemaining / 30.44;

  const currentAmount = vision.current_amount || 0;
  const targetAmount = vision.target_amount || 0;
  const remainingAmount = targetAmount - currentAmount;

  const monthlySavings = cashflow?.monthly_savings || 0;
  const requiredMonthly = remainingAmount / monthsRemaining;
  const savingsShortfall = Math.max(0, requiredMonthly - monthlySavings);

  const data = [];
  for (let month = 0; month <= 3; month++) {
    const optimizedSavings = monthlySavings + savingsShortfall * 0.5;
    data.push({
      monthLabel: month === 0 ? 'Now' : `Month ${month}`,
      current: Math.min(currentAmount + monthlySavings * month, targetAmount),
      optimized: Math.min(currentAmount + optimizedSavings * month, targetAmount),
      target: targetAmount,
    });
  }

  const formatCurrency = (value) => {
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
    return `$${value.toFixed(0)}`;
  };

  return (
    <div className="surface-panel p-4 border-t-2 border-t-pine">
      <h4 className="font-display text-h3 text-primary mb-3">3-month progress projection</h4>
      <p className="sr-only">
        Chart comparing current savings path, optimized path, and target amount over three months.
      </p>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid {...rechartsGrid} />
          <XAxis dataKey="monthLabel" {...rechartsXAxis} />
          <YAxis {...rechartsYAxis} tickFormatter={formatCurrency} />
          <Tooltip formatter={(v) => formatCurrency(v)} {...rechartsTooltip} />
          <Area
            type="monotone"
            dataKey="current"
            stroke={CHART_RAMP[1]}
            fill={CHART_RAMP[4]}
            fillOpacity={0.4}
            strokeWidth={2}
            name="Current path"
          />
          <Line
            type="monotone"
            dataKey="optimized"
            stroke={CHART_RAMP[0]}
            strokeWidth={2}
            dot={false}
            name="Optimized path"
          />
          <Line
            type="monotone"
            dataKey="target"
            stroke={CHART_NEUTRALS.inkFaint}
            strokeDasharray="4 4"
            dot={false}
            name="Target"
          />
        </AreaChart>
      </ResponsiveContainer>
      <div className="mt-3 flex flex-wrap gap-4 text-label text-secondary">
        <span className="flex items-center gap-2">
          <span className="w-3 h-0.5 bg-pine" aria-hidden /> Current path
        </span>
        <span className="flex items-center gap-2">
          <span className="w-3 h-0.5 border-t border-dashed border-pine" aria-hidden /> Optimized
        </span>
        <span className="flex items-center gap-2">
          <span className="w-3 h-0.5 border-t border-dashed border-hairline" aria-hidden /> Target
        </span>
      </div>
    </div>
  );
};

export default VisionStrategyChart;
